import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { hideLoading, showErrorMessage, showLoading, showMessage } from "@my-dld/utils/UIUtils";
import EjariApi from "@eres/ejari-api";
import { getPropertiesRowSearchQuery, getUnitsSearchQuery } from "@eres/ejari-api/api";
import { ProjectProperty } from "@eres/ejari-api/Entity/LeasedProperties";
import { RenderChangeableRentalStatus, RenderRentalStatus } from "./ProjectProperties";
import Strings from "@my-dld/assets/strings";
import { I8N } from "@eres/core";
import { AppConstants } from "../../../../configApp";
import { EjariPropertyType } from "@eres/ejari-api/Entity";
import { switchEjariUserMode } from "@eres/ejari-core/contexts/EjariContext";
import { navigateWithInParent } from "@my-dld/utils/StoreUtils";
import { LeasedContext, updateLeasedContext } from "@eres/ejari-core/contexts/LeasedContext";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import BuildingUnitsListView from "../../component/Leased/BuildingUnitsListView";
import BuildingUnitsGridView from "../../component/Leased/BuildingUnitsGridView";
import { GRID_SIZE } from "@my-dld/components/DataView/GridDataView";

interface Props extends RoutedViewProps<{ rentalStatus: string }> {

}

interface State {
	isLoading: boolean;
	properties?: ProjectProperty[];
	status: string;
	startIndex: number;
	totalRows: number;
}

class BuildingUnits extends Component<Props, State> {
	static contextType = LeasedContext;
	context!: React.ContextType<typeof LeasedContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			isLoading: true,
			status: props.match.params.rentalStatus,
			startIndex: 0,
			totalRows: 0,
		};
	}

	componentDidMount(): void {
		const { property, units } = this.context;
		if ( property && units ) {
			this.setState( {
				isLoading: false,
				properties: units,
				totalRows: units[ 0 ].TotalRecords!,
			} );
		} else if ( property ) {
			this.loadBuildingUnits();
		}
	}

	render() {
		const { property } = this.context;
		const { isLoading, properties, totalRows } = this.state;
		return <ListGridDataView
			isLoading={ isLoading }
			dataAvailable={ properties && properties.length > 0 }
			title={ Strings.dash_ejari_properties_title }
			renderListView={ this.renderListView }
			renderGridView={ this.renderGridView }
			extraController={ this.renderExtraController }
			totalCount={ totalRows }
			onPageChange={ this.onPageChange }>
			{ property && <RenderBuilding property={ property }/> }
		</ListGridDataView>;
	}

	private renderListView = () => {
		const { isLoading, properties } = this.state;
		return ( !isLoading ? <BuildingUnitsListView
			properties={ properties ? properties : [] }
			onPropertySelect={ this.onPropertySelect }/> : null );
	};

	private renderGridView = () => {
		const { isLoading, properties } = this.state;
		return ( !isLoading ? <BuildingUnitsGridView
			properties={ properties ? properties : [] }
			onPropertySelect={ this.onPropertySelect }/> : null );
	};

	private renderExtraController = () => {
		const { propertySearch } = this.context;
		const { status } = this.state;
		const searchStatus = ( propertySearch && propertySearch.PropertyStatus ) ? propertySearch.PropertyStatus : undefined;
		return <React.Fragment>
			<RenderChangeableRentalStatus
				status={ status }
				onStatusChange={ this.onStatusChange }
				searchStatus={ searchStatus }/>
			<RenderRentalStatus searchStatus={ searchStatus }/>
		</React.Fragment>;
	};

	private onPageChange = ( pageIndex: number ) => {
		this.setState( {
			startIndex: ( pageIndex - 1 ), // GRID_SIZE * ( pageIndex - 1 )
		}, () => {
			showLoading();
			this.loadBuildingUnits();
		} );
	};

	private onStatusChange = ( status: string ) => {
		this.setState( { status: status, startIndex: 0 }, () => {
			showLoading();
			this.loadBuildingUnits();
		} );
	};

	private loadBuildingUnits() {
		const { startIndex, status } = this.state;
		const { propertySearch, property } = this.context;
		if ( property ) {
			// const searchQuery = getUnitsSearchQuery( ( property.BuildingPropertyIdKey ?? property.BuildingPropertyId )!, startIndex, status, propertySearch );
			const searchQuery = getPropertiesRowSearchQuery( startIndex, status, property.RowValue );
			EjariApi.getBuildingUnits( searchQuery ).then( ( resp: any ) => {
				hideLoading();
				const properties: ProjectProperty[] = ( resp ? resp.Response : [] );
				this.setState( {
					isLoading: false,
					properties: properties,
					totalRows: properties.length > 0 ? properties[ 0 ].TotalRecords! : 0,
				} );
			} ).catch( e => {
				hideLoading();
				showErrorMessage( e );
			} );
		}
	}

	private onPropertySelect = ( property: ProjectProperty ) => {
		updateLeasedContext( { units: this.state.properties } );
		switchEjariUserMode( "owner" );
		navigateWithInParent( "mydld",
			`leased/property/${ property.PropertyTypeId }/${ encodeURIComponent( property.RowValue ?? "" ) }/${ property.IsFreeHold }/${ true }/ejarisvc` );
	};
}

function RenderBuilding( props: { property: ProjectProperty } ) {
	const { property } = props;
	let icon = "land";
	switch ( property.PropertyTypeId ) {
		case EjariPropertyType.VILLA:
			icon = "villa";
			break;
		case EjariPropertyType.BUILDING:
			icon = "building";
			break;
		case EjariPropertyType.UNIT:
			icon = "unit";
			break;
	}
	return <div>
		<div className="property-info-summary">
			<div>
				<div className="property-icon">
					<img src={ AppConstants.rootUrl + "assets/property-icons/" + icon + ".svg" }/>
				</div>
			</div>
			<div>
				<span>{ I8N.isRTL() ? property.BuildingNameAr : property.BuildingNameEn }</span>
				<span>{ Strings.building_name }</span>
			</div>
			<div>
				<span>{ property.BuildingUnitsCount + " " + Strings.units }</span>
				<span>{ Strings.no_of_properties }</span>
			</div>
		</div>
	</div>;
}

export default BuildingUnits;
